import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useGetDownlineLedgerQuery, useGetUserTypeLedgerQuery } from '../services/ledger/ledgerApi';
import { getNextLevelUserType, canNavigateDown, formatUserTypeLabel } from '../utils/downlineHelpers';
import { USER_LEVELS } from '../config/menuItems';
import Loading from '../components/Loading';

function UserDownlineLedgerPage() {
  const navigate = useNavigate();
  const { userId, username, userType: userTypeParam } = useParams();

  // Convert userType string to number based on USER_LEVELS
  const getUserTypeNumber = (typeString) => {
    const typeMap = {
      'SuperAdmin': USER_LEVELS.SUPERADMIN,
      'MiniAdmin': USER_LEVELS.MINIADMIN,
      'Master': USER_LEVELS.MASTER,
      'Super': USER_LEVELS.SUPER,
      'Agent': USER_LEVELS.AGENT,
      'Client': USER_LEVELS.CLIENT
    };
    return typeMap[typeString] || USER_LEVELS.CLIENT;
  };

  const currentUserTypeLevel = getUserTypeNumber(userTypeParam);
  const childUserTypeLevel = getNextLevelUserType(currentUserTypeLevel);
  const childUserTypeString = formatUserTypeLabel(childUserTypeLevel);

  // Fetch ledger data for the child level users
  const { data: response, isLoading, error, isFetching } = useGetDownlineLedgerQuery({ 
    userId,
    userType: childUserTypeString 
  });

  console.log('UserDownlineLedger API Response:', response);
  console.log('Current User Type:', userTypeParam, 'Level:', currentUserTypeLevel);
  console.log('Child User Type:', childUserTypeString, 'Level:', childUserTypeLevel);

  // Get users array from response
  const users = Array.isArray(response?.users) ? response.users : [];
  const hasDownline = users.length > 0;

  // If no downline users, show message
  const showNoDownlineMessage = !isLoading && !isFetching && !error && !hasDownline;

  // Separate users into categories based on their balance
  const lenaUsers = users.filter(user => {
    const balanceStr = user.balance || '';
    return balanceStr.includes('Lena');
  });

  const denaUsers = users.filter(user => {
    const balanceStr = user.balance || '';
    return balanceStr.includes('Dena');
  });

  const clearUsers = users.filter(user => {
    const netBalance = user.netBalance || 0;
    return netBalance === 0;
  });

  // Calculate totals
  const lenaTotal = lenaUsers.reduce((sum, user) => sum + Math.abs(user.netBalance || 0), 0);
  const denaTotal = denaUsers.reduce((sum, user) => sum + Math.abs(user.netBalance || 0), 0);
  const clearTotal = 0;

  // Map to display format
  const lenaData = lenaUsers.map((user, index) => ({
    no: index + 1,
    username: user.username,
    userId: user.userId,
    fullname: user.fullname,
    userType: user.userType,
    balance: Math.abs(user.netBalance || 0),
  }));

  const denaData = denaUsers.map((user, index) => ({
    no: index + 1,
    username: user.username,
    userId: user.userId,
    fullname: user.fullname,
    userType: user.userType,
    balance: Math.abs(user.netBalance || 0),
  }));

  const clearData = clearUsers.map((user, index) => ({
    no: index + 1,
    username: user.username,
    userId: user.userId,
    fullname: user.fullname,
    userType: user.userType,
    balance: 0,
  }));

  const handleTransactions = (userId) => {
    navigate(`/ledger/master-transactions/${userId}`);
  };

  const handleSettle = (userId) => {
    console.log('Settle for:', userId);
    // Open settle modal
  };

  const handleUserClick = (childUserId, childUsername, childUserType) => {
    // Check if can navigate further down
    if (canNavigateDown(getUserTypeNumber(childUserType))) {
      // Navigate to this child user's downline
      navigate(`/ledger/user-downline/${childUserId}/${childUsername}/${childUserType}`);
    }
  };

  const getTitle = () => {
    return `${username || 'User'} (${userTypeParam || ''}) - ${childUserTypeString} Ledger`;
  };

  return (
    <div className="">
      <div className="bg-white rounded shadow">
        {/* Header */}
        <div className="bg-[#2c3e3e] text-white px-5 py-3 flex items-center justify-between">
          <h2 className="text-xl font-normal">{getTitle()}</h2>
          <button 
            onClick={() => navigate(-1)}
            className="bg-blue-500 text-white px-6 py-2 hover:bg-blue-600"
          >
            Back
          </button>
        </div>

        {/* Three Column Layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-5">
          {(isLoading || isFetching) ? (
            <div className="col-span-3 p-8 flex justify-center">
              <Loading />
            </div>
          ) : error ? (
            <div className="col-span-3 p-4 text-red-500 text-center">
              {error.message || 'Failed to load data'}
            </div>
          ) : showNoDownlineMessage ? (
            <div className="col-span-3 p-8 text-center text-gray-500">
              <div className="text-xl mb-2">No downline users found for this user</div>
              <div className="text-sm">This user may not have any child users or may be at the lowest hierarchy level.</div>
            </div>
          ) : (
            <>
              {/* Lena Section */}
              <div>
            <div className="bg-green-500 text-white flex justify-between" style={{ fontSize: '24px', fontWeight: 400, paddingLeft: '10px', paddingRight: '10px', borderRadius: '5px' }}>
              <span>Lena</span>
              <span>{lenaTotal.toFixed(2)}</span>
            </div>
            <table className="w-full border-collapse mt-2">
              <thead className="bg-[#2c3e3e] text-white">
                <tr>
                  <th className="p-2 border border-gray-600 text-left text-sm">S.No</th>
                  <th className="p-2 border border-gray-600 text-left text-sm">User Detail</th>
                  <th className="p-2 border border-gray-600 text-left text-sm">Balance</th>
                  <th className="p-2 border border-gray-600 text-left text-sm">Details</th>
                </tr>
              </thead>
              <tbody>
                {lenaData.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="p-4 text-center text-gray-400">
                      No Data
                    </td>
                  </tr>
                ) : (
                  lenaData.map((item, index) => (
                    <tr key={index} className="bg-white hover:bg-gray-50">
                      <td className="p-2 border border-gray-300">{item.no}</td>
                      <td className="p-2 border border-gray-300">
                        <div 
                          className="text-blue-600 cursor-pointer hover:underline"
                          onClick={() => handleUserClick(item.userId, item.username, item.userType)}
                        >
                          {item.username}
                          <div className="text-xs text-gray-600">({item.fullname})</div>
                          <div className="text-xs">👁</div>
                        </div>
                      </td>
                      <td className="p-2 border border-gray-300">{item.balance}</td>
                      <td className="p-2 border border-gray-300">
                        <div className="flex flex-col gap-1">
                          <button 
                            onClick={() => handleTransactions(item.userId)}
                            className="bg-orange-500 text-white px-2 py-1 text-xs hover:bg-orange-600"
                          >
                            Transanctions
                          </button>
                          <button 
                            onClick={() => handleSettle(item.userId)}
                            className="bg-orange-600 text-white px-2 py-1 text-xs hover:bg-orange-700"
                          >
                            SETTLE
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Dena Section */}
          <div>
            <div className="text-white flex justify-between" style={{ backgroundColor: '#de4e4d', fontSize: '24px', fontWeight: 400, paddingLeft: '10px', paddingRight: '10px', borderRadius: '5px' }}>
              <span>Dena</span>
              <span>{denaTotal.toFixed(2)}</span>
            </div>
            <table className="w-full border-collapse mt-2">
              <thead className="bg-[#2c3e3e] text-white">
                <tr>
                  <th className="p-2 border border-gray-600 text-left text-sm">S.No</th>
                  <th className="p-2 border border-gray-600 text-left text-sm">User Detail</th>
                  <th className="p-2 border border-gray-600 text-left text-sm">Balance</th>
                  <th className="p-2 border border-gray-600 text-left text-sm">Details</th>
                </tr>
              </thead>
              <tbody>
                {denaData.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="p-4 text-center text-gray-400">
                      No Data
                    </td>
                  </tr>
                ) : (
                  denaData.map((item, index) => (
                    <tr key={index} className="bg-white hover:bg-gray-50">
                      <td className="p-2 border border-gray-300">{item.no}</td>
                      <td className="p-2 border border-gray-300">
                        <div 
                          className="text-blue-600 cursor-pointer hover:underline"
                          onClick={() => handleUserClick(item.userId, item.username, item.userType)}
                        >
                          {item.username}
                          <div className="text-xs text-gray-600">({item.fullname})</div>
                          <div className="text-xs">👁</div>
                        </div>
                      </td>
                      <td className="p-2 border border-gray-300">{item.balance}</td>
                      <td className="p-2 border border-gray-300">
                        <div className="flex flex-col gap-1">
                          <button 
                            onClick={() => handleTransactions(item.userId)}
                            className="bg-orange-500 text-white px-2 py-1 text-xs hover:bg-orange-600"
                          >
                            Transanctions
                          </button>
                          <button 
                            onClick={() => handleSettle(item.userId)}
                            className="bg-orange-600 text-white px-2 py-1 text-xs hover:bg-orange-700"
                          >
                            SETTLE
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Clear Section */}
          <div>
            <div className="bg-blue-400 text-white flex justify-between" style={{ fontSize: '24px', fontWeight: 400, paddingLeft: '10px', paddingRight: '10px', borderRadius: '5px' }}>
              <span>Clear</span>
              <span>{clearTotal.toFixed(2)}</span>
            </div>
            <table className="w-full border-collapse mt-2">
              <thead className="bg-[#2c3e3e] text-white">
                <tr>
                  <th className="p-2 border border-gray-600 text-left text-sm">S.No</th>
                  <th className="p-2 border border-gray-600 text-left text-sm">User Detail</th>
                  <th className="p-2 border border-gray-600 text-left text-sm">Balance</th>
                  <th className="p-2 border border-gray-600 text-left text-sm">Details</th>
                </tr>
              </thead>
              <tbody>
                {clearData.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="p-4 text-center text-gray-400">
                      No Data
                    </td>
                  </tr>
                ) : (
                  clearData.map((item, index) => (
                    <tr key={index} className="bg-white hover:bg-gray-50">
                      <td className="p-2 border border-gray-300">{item.no}</td>
                      <td className="p-2 border border-gray-300">
                        <div 
                          className="text-blue-600 cursor-pointer hover:underline"
                          onClick={() => handleUserClick(item.userId, item.username, item.userType)}
                        >
                          {item.username}
                          <div className="text-xs text-gray-600">({item.fullname})</div>
                          <div className="text-xs">👁</div>
                        </div>
                      </td>
                      <td className="p-2 border border-gray-300">{item.balance}</td>
                      <td className="p-2 border border-gray-300">
                        <div className="flex flex-col gap-1">
                          <button 
                            onClick={() => handleTransactions(item.userId)}
                            className="bg-orange-500 text-white px-2 py-1 text-xs hover:bg-orange-600"
                          >
                            Transanctions
                          </button>
                          <button 
                            onClick={() => handleSettle(item.userId)}
                            className="bg-orange-600 text-white px-2 py-1 text-xs hover:bg-orange-700"
                          >
                            SETTLE
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default UserDownlineLedgerPage;
